import React from 'react';
import { Container, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faRedo } from '@fortawesome/free-solid-svg-icons';
import './ErrorBoundary.css';

/**
 * General error boundary component for catching React errors
 * Provides fallback UI and error reporting capabilities
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Report error to logging service if configured
    this.reportError(error, errorInfo);
  }

  reportError = (error, errorInfo) => {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('Error Boundary Report');
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }

    // In production, this would send to an error tracking service
    // Example: Sentry, LogRocket, CloudWatch, etc.
    try {
      const errorReport = {
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      // TODO: Send to error tracking service
      // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
      
      // For now, just log to console
      console.log('Error report:', errorReport);
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI from props
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Container className="error-boundary-container">
          <div className="error-boundary-content">
            <Alert variant="danger" className="error-boundary-alert">
              <div className="error-icon">
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
              </div>
              <Alert.Heading>Something went wrong</Alert.Heading>
              <p>
                We're sorry, but something unexpected happened. The error has been logged
                and we'll look into it.
              </p>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="error-details">
                  <summary>Error Details (Development Only)</summary>
                  <pre className="error-message">
                    {this.state.error.toString()}
                  </pre>
                  {this.state.errorInfo && (
                    <pre className="error-stack">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </details>
              )}

              <div className="error-actions">
                <Button 
                  variant="primary" 
                  onClick={this.handleReset}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faRedo} className="me-2" />
                  Try Again
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>
              </div>

              {this.state.errorCount > 1 && (
                <Alert variant="warning" className="mt-3">
                  <small>
                    This error has occurred {this.state.errorCount} times. 
                    If the problem persists, please contact support.
                  </small>
                </Alert>
              )}
            </Alert>
          </div>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
